// src/context/ModalContext.tsx
import React, { createContext, useContext, useState, useMemo, useEffect, useRef, ReactNode, MutableRefObject } from 'react';
import styled from 'styled-components';
import Button from '../components/common/Button';
import Icon from '../components/icons/Icons';
import { formatValidationErrors } from '../utils/errorUtils';

// Modal types for your application
export type ModalType = 'form' | 'notification' | 'confirmation';

// Modal positions
export type ModalPosition = 'center' | 'right';

// Interface for the modal content props
interface ModalContentProps {
  onClose: () => void;
  [key: string]: any; // Additional props passed to the modal content
}

// Interface for the modal context
interface ModalContextType {
  openModal: (type: ModalType, props?: any) => void;
  closeModal: () => void;
  showSuccess: (title: string, message: string) => void;
  showError: (title: string, message: string) => void;
  showModalError: (message: string) => void;
  clearModalError: () => void;
  isOpen: boolean;
  modalType: ModalType | null;
  modalProps: any;
  modalError: string | null;
  // New superior error modal state
  errorModalOpen: boolean;
  errorModalMessage: string | null;
  // Submit handler ref — form components register their submit fn here
  submitHandlerRef: MutableRefObject<(() => void) | null>;
}

// Stable ref placeholder for the context default — replaced by real ref in ModalProvider
const _defaultSubmitHandlerRef: MutableRefObject<(() => void) | null> = { current: null };

// Create context with default values
const ModalContext = createContext<ModalContextType>({
  openModal: () => {},
  closeModal: () => {},
  showSuccess: () => {},
  showError: () => {},
  showModalError: () => {},
  clearModalError: () => {},
  isOpen: false,
  modalType: null,
  modalProps: {},
  modalError: null,
  errorModalOpen: false,
  errorModalMessage: null,
  submitHandlerRef: _defaultSubmitHandlerRef,
});

// Styled components for the modal
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  z-index: 1000;
`;

const ModalWrapper = styled.div<{ position: ModalPosition }>`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: ${({ position }) => position === 'center' ? 'center' : 'flex-end'};
  align-items: ${({ position }) => position === 'center' ? 'center' : 'stretch'};
`;

const ModalContainer = styled.div<{ modalType: ModalType | null }>`
  background-color: white;
  box-shadow: ${({ theme }) => theme.shadows?.medium || '0px 4px 20px rgba(0, 0, 0, 0.15)'};
  width: ${({ modalType }) => 
    modalType === 'form' ? '580px' : 
    modalType === 'notification' ? '460px' : '460px'};
  max-width: ${({ modalType }) => 
    modalType === 'form' ? 'none' : '90%'};
  max-height: ${({ modalType }) => 
    modalType === 'form' ? '96%' : '90vh'};
  overflow-y: clip;
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  
  ${({ modalType }) => 
    modalType === 'notification' ? `
      border-radius: 0.5rem;
    ` : 
    modalType === 'form' ? `
      height: 100%;
    ` : `
      border-radius: 0.5rem;
    `}
    ${({ modalType }) => 
        modalType === 'form' ? `  margin: 1rem;` :  ""}
`;

// Common modal header styling
const ModalHeader = styled.div`
  padding: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #000;
`;

const ModalDescription = styled.p`
  font-size: 0.875rem;
  color: #666;
  line-height: 1.5;
`;

const FormContainer = styled.div`
  padding: 0 1.5rem;
  flex: 1;
  overflow-y: auto;
`;

// Function to determine modal position based on type
const getModalPosition = (type: ModalType | null): ModalPosition => {
  switch (type) {
    case 'form':
      return 'right';
    case 'notification':
    case 'confirmation':
    default:
      return 'center';
  }
};

// ─── Accessible modal overlay with focus trap and Escape key support ──────────
interface AccessibleModalOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  labelId: string;
  children: ReactNode;
}

const AccessibleModalOverlay: React.FC<AccessibleModalOverlayProps> = ({
  isOpen,
  onClose,
  children,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  const FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Close on Escape; trap Tab/Shift+Tab inside the modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'Tab' && overlayRef.current) {
        const focusable = Array.from(
          overlayRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus the first focusable element when modal opens
  useEffect(() => {
    if (!isOpen || !overlayRef.current) return;
    const focusable = overlayRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    focusable[0]?.focus();
  }, [isOpen]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <ModalOverlay ref={overlayRef} isOpen={isOpen} onClick={onClose}>
      {children}
    </ModalOverlay>
  );
};

// ─── Modal provider ───────────────────────────────────────────────────────────
export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalType, setModalType] = useState<ModalType | null>(null);
  const [modalProps, setModalProps] = useState<any>({});
  const [modalError, setModalError] = useState<string | null>(null);

  // Ref that form components register their submit handler into
  const submitHandlerRef = useRef<(() => void) | null>(null);

  // NEW: Superior error modal state
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState<string | null>(null);

  const openModal = (type: ModalType, props = {}) => {
    setModalType(type);
    setModalProps(props);
    setModalError(null);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setModalError(null);
    setTimeout(() => {
      setModalType(null);
      setModalProps({});
    }, 300);
  };

  const showModalError = (message: string) => {
    setModalError(message);
  };

  const clearModalError = () => {
    setModalError(null);
  };

  // NEW: Superior error modal functions
  const closeErrorModal = () => {
    setErrorModalOpen(false);
    setErrorModalMessage(null);
  };

  const showSuccess = (title: string, message: string) => {
    setIsOpen(false);
    setTimeout(() => {
      setModalType('notification');
      setModalProps({
        title,
        message,
        icon: <Icon name='success' />,
        buttonText: 'Continue'
      });
      setIsOpen(true);
    }, 400);
  };

  const showError = (_title: string, message: string) => {
    const formattedMessage =
      typeof message === 'string' ? formatValidationErrors(message) : message;
    setErrorModalMessage(formattedMessage);
    setErrorModalOpen(true);
  };

  const position = getModalPosition(modalType);

  const contextValue = useMemo(
    () => ({
      openModal,
      closeModal,
      showSuccess,
      showError,
      showModalError,
      clearModalError,
      isOpen,
      modalType,
      modalProps,
      modalError,
      errorModalOpen,
      errorModalMessage,
      submitHandlerRef,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen, modalType, modalProps, modalError, errorModalOpen, errorModalMessage]
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      
      {/* Regular Modal System */}
      <AccessibleModalOverlay
        isOpen={isOpen}
        onClose={closeModal}
        labelId="modal-title"
      >
        <ModalWrapper position={position}>
          <ModalContainer
            modalType={modalType}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            {isOpen && modalType && (
              <ModalContent
                type={modalType}
                onClose={closeModal}
                modalError={modalError}
                {...modalProps}
              />
            )}
          </ModalContainer>
        </ModalWrapper>
      </AccessibleModalOverlay>

      {/* Error Modal System */}
      <SuperiorErrorOverlay
        isOpen={errorModalOpen}
        onClick={(e) => e.target === e.currentTarget && closeErrorModal()}
      >
        <SuperiorErrorContainer
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="error-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <SuperiorErrorHeader>
            <SuperiorErrorIcon aria-hidden="true">!</SuperiorErrorIcon>
            <SuperiorErrorContent>
              <SuperiorErrorTitle id="error-modal-title">Error</SuperiorErrorTitle>
              <SuperiorErrorMessage>{errorModalMessage}</SuperiorErrorMessage>
            </SuperiorErrorContent>
          </SuperiorErrorHeader>
          <SuperiorErrorActions>
            <SuperiorErrorCloseButton onClick={closeErrorModal}>
              Cancel
            </SuperiorErrorCloseButton>
            <SuperiorErrorButton onClick={closeErrorModal}>
              OK
            </SuperiorErrorButton>
          </SuperiorErrorActions>
        </SuperiorErrorContainer>
      </SuperiorErrorOverlay>
    </ModalContext.Provider>
  );
};

// Hook for using the modal context
export const useModal = () => useContext(ModalContext);

// Component that renders the appropriate modal content based on type
const ModalContent: React.FC<{ type: ModalType; onClose: () => void; modalError?: string | null; [key: string]: any }> = ({ 
  type, 
  onClose,
  modalError,
  ...props 
}) => {
  switch (type) {
    case 'form':
      return <FormModal onClose={onClose} modalError={modalError} {...props} />;
    case 'notification':
      return <NotificationModal onClose={onClose} {...props} />;
    case 'confirmation':
      return <ConfirmationModal onClose={onClose} {...props} />;
    default:
      return null;
  }
};

// ─── Styled components for modal sub-components (module scope) ────────────────
const FormButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  box-shadow: 0px -4px 4px 0px #0000000D;
`;

const NotificationContainer = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const NotificationIconContainer = styled.div`
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;

  svg {
    color: white;
    width: 32px;
    height: 32px;
  }
`;

const NotificationTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const NotificationMessage = styled.p`
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const ConfirmationContainer = styled.div`
  padding: 1.5rem;
`;

const ConfirmationTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const ConfirmationMessage = styled.p`
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const ConfirmationButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

// Form Modal - Remove the inline error banner since we're using superior modal
const FormModal: React.FC<ModalContentProps & { showSubmitButton?: boolean; modalError?: string | null }> = ({
  onClose,
  title,
  description,
  content,
  primaryButtonText = 'Submit',
  secondaryButtonText = 'Cancel',
  showSubmitButton = true,
  modalError, // Keep for backward compatibility but don't use
  children
}) => {
  const { submitHandlerRef } = useModal();

  const handleSubmit = () => {
    submitHandlerRef.current?.();
  };

  const truncateMiddle = (str: string, maxLength: number = 50) => {
    if (str.length <= maxLength) return str;
    
    const start = Math.ceil(maxLength / 2) - 2;
    const end = Math.floor(maxLength / 2) - 2;
    
    return `${str.slice(0, start)}...${str.slice(-end)}`;
  };
  
  return (
    <>
      <ModalHeader>
        <ModalTitle id="modal-title">{truncateMiddle(title, 55)}</ModalTitle>
        {description && <ModalDescription>{description}</ModalDescription>}
      </ModalHeader>
      
      <FormContainer>
        {content || children}
      </FormContainer>
      
      <FormButtonContainer>
        {showSubmitButton && (
          <Button onClick={handleSubmit} variant='primary' fullWidth>
            {primaryButtonText}
          </Button>
        )}
        <Button onClick={onClose} variant='outline' fullWidth>
          {secondaryButtonText}
        </Button>
      </FormButtonContainer>
    </>
  );
};

const NotificationModal: React.FC<ModalContentProps> = ({
  onClose,
  title = 'Notification',
  message,
  icon,
  buttonText = 'Continue',
  children,
  content
}) => {
  return content || children || (
    <NotificationContainer role="alert" aria-live="assertive">
      {icon && <NotificationIconContainer>{icon}</NotificationIconContainer>}
      <NotificationTitle>{title}</NotificationTitle>
      <NotificationMessage>{message}</NotificationMessage>
      <Button onClick={onClose} variant='primary' fullWidth>
        {buttonText}
      </Button>
    </NotificationContainer>
  );
};

const ConfirmationModal: React.FC<ModalContentProps> = ({
  onClose,
  title = 'Confirmation',
  message,
  onConfirm,
  confirmButtonText,
  cancelButtonText,
  // Accept form-modal aliases so call sites don't need to be updated
  primaryButtonText,
  secondaryButtonText,
  children,
  content
}) => {
  const resolvedConfirmText = confirmButtonText ?? primaryButtonText ?? 'Confirm';
  const resolvedCancelText = cancelButtonText ?? secondaryButtonText ?? 'Cancel';
  const [isConfirming, setIsConfirming] = React.useState(false);

  const handleConfirm = async () => {
    if (isConfirming) return;
    setIsConfirming(true);
    try {
      if (onConfirm) await onConfirm();
      onClose();
    } catch {
      setIsConfirming(false);
    }
  };

  return content || children || (
    <ConfirmationContainer>
      <ConfirmationTitle>{title}</ConfirmationTitle>
      <ConfirmationMessage>{message}</ConfirmationMessage>
      <ConfirmationButtonsContainer>
        <Button onClick={onClose} variant='outline' fullWidth disabled={isConfirming}>
          {resolvedCancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          variant='primary'
          fullWidth
          disabled={isConfirming}
        >
          {isConfirming ? 'Please wait...' : resolvedConfirmText}
        </Button>
      </ConfirmationButtonsContainer>
    </ConfirmationContainer>
  );
};

// NEW: Superior Error Modal styled components
const SuperiorErrorOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 2000; /* Higher than regular modals */
`;

const SuperiorErrorContainer = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0px 10px 40px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const SuperiorErrorHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  border-bottom: 1px solid #fee;
  background-color: #fef2f2;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const SuperiorErrorIcon = styled.div`
  width: 24px;
  height: 24px;
  background-color: #dc2626;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
`;

const SuperiorErrorContent = styled.div`
  flex: 1;
`;

const SuperiorErrorTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #dc2626;
  margin: 0 0 0.5rem 0;
`;

const SuperiorErrorMessage = styled.p`
  color: #991b1b;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-line;
  margin: 0;
`;

const SuperiorErrorActions = styled.div`
  padding: 1rem 1.5rem 1.5rem 1.5rem;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const SuperiorErrorButton = styled.button`
  background-color: #dc2626;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  font-size: 0.875rem;
  
  &:hover {
    background-color: #b91c1c;
  }
`;

const SuperiorErrorCloseButton = styled.button`
  background: none;
  border: 1px solid #d1d5db;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
  
  &:hover {
    background-color: #f9fafb;
  }
`;